import i18nFactory from '@yandex-int/i18n';
import moment from 'moment';

import { Shape2Icon } from '../../components/Shape2/Shape2';
import {
    CampaignType,
    StatusInfo,
    TimeTargetingSettings,
    TimeTargetingStatus
} from '../../typings/domain';
import {
    GdCampaignPrimaryStatus,
    GdCampaignPrimaryStatusDesc,
    GdWalletAction
} from '../../typings/graphql-grid';
import { StatusHint } from '../../typings/grid';
import { UrlHelpers } from '../UrlHelpers';

import * as keyset from './CampaignHelpers.i18n';

const helperI18N = i18nFactory(keyset);

/**
 * Возвращает иконку для типа кампании
 */
export function getIconNameForCampaignType(type: CampaignType): Shape2Icon {
    switch (type) {
        case CampaignType.TEXT:
            return Shape2Icon.typeText;
        case CampaignType.PERFORMANCE:
            return Shape2Icon.typePerformance;
        case CampaignType.DYNAMIC:
            return Shape2Icon.typeDynamic;
        case CampaignType.MOBILE_CONTENT:
            return Shape2Icon.typeMobileContent;
        case CampaignType.MCBANNER:
            return Shape2Icon.typeMCBanner;
        case CampaignType.CPM_BANNER:
            return Shape2Icon.typeCPMCampaign;
        case CampaignType.CPM_DEALS:
            return Shape2Icon.typeCPMDeals;
        case CampaignType.CONTENT_PROMOTION:
            return Shape2Icon.typeContentPromotion;
        case CampaignType.CPM_YNDX_FRONTPAGE:
            return Shape2Icon.typeCPMFrontpage;
        case CampaignType.INTERNAL_DISTRIB:
            return Shape2Icon.typeInternalDistrib;
        case CampaignType.INTERNAL_FREE:
            return Shape2Icon.typeInternalFree;
        default:
            throw new Error(
                `Для кампании с типом ${type} не определена иконка`
            );
    }
}

export const CAMPAIGN_TYPES_ORDER = [
    CampaignType.TEXT,
    CampaignType.CPM_BANNER,
    CampaignType.MOBILE_CONTENT,
    CampaignType.PERFORMANCE,
    CampaignType.MCBANNER,
    CampaignType.DYNAMIC,
    CampaignType.CPM_DEALS,
    CampaignType.CPM_YNDX_FRONTPAGE,
    CampaignType.INTERNAL_DISTRIB,
    CampaignType.INTERNAL_FREE,
    CampaignType.CONTENT_PROMOTION
];

export function isBetaCampaignType(type: CampaignType): boolean {
    return (
        type === CampaignType.CPM_DEALS ||
        type === CampaignType.CONTENT_PROMOTION ||
        type === CampaignType.CPM_YNDX_FRONTPAGE
    );
}

export function getTitleForCampaignType(type: CampaignType): string {
    const campaignTypeDictionary = {
        [CampaignType.TEXT]: helperI18N('campaign-type-text'),
        [CampaignType.PERFORMANCE]: helperI18N('campaign-type-performance'),
        [CampaignType.DYNAMIC]: helperI18N('campaign-type-dynamic'),
        [CampaignType.MOBILE_CONTENT]: helperI18N(
            'campaign-type-mobile_content'
        ),
        [CampaignType.MCBANNER]: helperI18N('campaign-type-mcbanner'),
        [CampaignType.CPM_BANNER]: helperI18N('campaign-type-cpm_banner'),
        [CampaignType.CPM_DEALS]: helperI18N('campaign-type-cpm_deals'),
        [CampaignType.CONTENT_PROMOTION]: helperI18N(
            'campaign-type-content_promotion'
        ),
        [CampaignType.CPM_YNDX_FRONTPAGE]: helperI18N(
            'campaign-type-cpm_yndx_frontpage'
        ),
        [CampaignType.INTERNAL_DISTRIB]: helperI18N(
            'campaign-type-internal_distrib'
        ),
        [CampaignType.INTERNAL_FREE]: helperI18N('campaign-type-internal_free')
    };

    if (!campaignTypeDictionary[type]) {
        throw new Error(`Для значения ${type} не определено название`);
    }

    return campaignTypeDictionary[type];
}

export function getDescriptionForCampaignType(type: CampaignType): string {
    const campaignTypeDictionary = {
        [CampaignType.TEXT]: helperI18N('description-text'),
        [CampaignType.PERFORMANCE]: helperI18N('description-performance'),
        [CampaignType.DYNAMIC]: helperI18N('description-dynamic'),
        [CampaignType.MOBILE_CONTENT]: helperI18N('description-mobile_content'),
        [CampaignType.MCBANNER]: helperI18N('description-mcbanner'),
        [CampaignType.CPM_BANNER]: helperI18N('description-cpm_banner'),
        [CampaignType.CPM_DEALS]: helperI18N('description-cpm_deals'),
        [CampaignType.CONTENT_PROMOTION]: helperI18N(
            'description-content_promotion'
        ),
        [CampaignType.CPM_YNDX_FRONTPAGE]: helperI18N(
            'description-cpm_yndx_frontpage'
        ),
        [CampaignType.INTERNAL_DISTRIB]: helperI18N(
            'description-internal_distrib'
        ),
        [CampaignType.INTERNAL_FREE]: helperI18N('description-internal_free')
    };

    if (!campaignTypeDictionary[type]) {
        throw new Error(`Для значения ${type} не определено описание`);
    }

    return campaignTypeDictionary[type];
}

export function getHintForCampaignType(type: CampaignType): string {
    const campaignTypeDictionary = {
        [CampaignType.TEXT]: helperI18N('hint-text'),
        [CampaignType.PERFORMANCE]: helperI18N('hint-performance'),
        [CampaignType.DYNAMIC]: helperI18N('hint-dynamic'),
        [CampaignType.MOBILE_CONTENT]: helperI18N('hint-mobile_content'),
        [CampaignType.MCBANNER]: helperI18N('hint-mcbanner'),
        [CampaignType.CPM_BANNER]: helperI18N('hint-cpm_banner'),
        [CampaignType.CPM_DEALS]: helperI18N('hint-cpm_deals'),
        [CampaignType.CONTENT_PROMOTION]: helperI18N('hint-content_promotion'),
        [CampaignType.CPM_YNDX_FRONTPAGE]: helperI18N(
            'hint-cpm_yndx_frontpage'
        ),
        [CampaignType.INTERNAL_DISTRIB]: helperI18N('hint-internal_distrib'),
        [CampaignType.INTERNAL_FREE]: helperI18N('hint-internal_free')
    };

    if (!campaignTypeDictionary[type]) {
        throw new Error(
            `Для значения ${type} не определено дополнительное описание`
        );
    }

    return campaignTypeDictionary[type];
}

/**
 * Возвращает название для статуса кампании по его типу
 */
export function getCampaignStatusTitleByType(
    statusType: GdCampaignPrimaryStatus
): string {
    const dictionary = {
        [GdCampaignPrimaryStatus.ARCHIVED]: helperI18N('archived'),
        [GdCampaignPrimaryStatus.STOPPED]: helperI18N('stopped'),
        [GdCampaignPrimaryStatus.NO_MONEY]: helperI18N('moneyFinished'),
        [GdCampaignPrimaryStatus.MODERATION_DENIED]: helperI18N(
            'moderationDenied'
        ),
        [GdCampaignPrimaryStatus.TEMPORARILY_PAUSED]: helperI18N(
            'temporarilyPaused'
        ),
        [GdCampaignPrimaryStatus.ACTIVE]: helperI18N('started'),
        [GdCampaignPrimaryStatus.MODERATION]: helperI18N('moderationPending'),
        [GdCampaignPrimaryStatus.DRAFT]: helperI18N('draft')
    };

    if (!dictionary[statusType]) {
        throw new Error(
            `Для статуса кампании ${statusType} не определено название`
        );
    }

    return dictionary[statusType];
}

/**
 * Возвращает дополнительную информацию о статусе кампании
 */
export function getCampaignStatusHint(
    statusInfo: StatusInfo,
    campaignId: string,
    chiefLogin: string,
    agencyId: string | null,
    canPay: boolean,
    walletActions: Array<GdWalletAction>,
    startDate: moment.Moment,
    endDate?: moment.Moment
): StatusHint | undefined {
    const { hintType, timeTargetingSettings } = statusInfo;

    if (!hintType) {
        return;
    }

    const canPayIntoWallet = walletActions.includes(GdWalletAction.PAY);
    const canEditWallet = walletActions.includes(GdWalletAction.EDIT);

    switch (hintType) {
        case GdCampaignPrimaryStatusDesc.IS_IN_PROGRESS:
            return {
                text: helperI18N('isInProgress')
            };
        case GdCampaignPrimaryStatusDesc.IS_NOT_RECOVERED:
            return {
                text: helperI18N('isNotRecovered')
            };
        case GdCampaignPrimaryStatusDesc.ADD_MONEY:
            return canPay
                ? {
                      text: helperI18N('addMoney'),
                      link: UrlHelpers.getPayUrl(campaignId, chiefLogin)
                  }
                : undefined;
        case GdCampaignPrimaryStatusDesc.ADD_MONEY_TO_WALLET:
            return canPayIntoWallet
                ? {
                      text: helperI18N('addMoney'),
                      link: UrlHelpers.getWalletUrl(chiefLogin, agencyId)
                  }
                : undefined;
        case GdCampaignPrimaryStatusDesc.IS_PAUSED_BY_DAY_BUDGET:
            return {
                text: helperI18N('changeCampaignDayBudget')
            };
        case GdCampaignPrimaryStatusDesc.IS_PAUSED_BY_WALLET_DAY_BUDGET:
            return canEditWallet
                ? {
                      text: helperI18N('changeWalletDayBudget'),
                      link: UrlHelpers.getWalletUrl(chiefLogin, agencyId)
                  }
                : undefined;
        case GdCampaignPrimaryStatusDesc.IS_WAITING_START:
            return {
                text: helperI18N('willStartOnDate', {
                    date: startDate.format('L')
                })
            };
        case GdCampaignPrimaryStatusDesc.IS_PAUSED_BY_TIMETARGETING:
            return timeTargetingSettings
                ? {
                      text: getTimeTargetingStatusHint(timeTargetingSettings)
                  }
                : undefined;
        case GdCampaignPrimaryStatusDesc.IS_OVER:
            return endDate
                ? {
                      text: helperI18N('isOverOnDate', {
                          date: endDate.format('L')
                      })
                  }
                : undefined;
        case GdCampaignPrimaryStatusDesc.NO_ACTIVE_BANNERS:
            return {
                text: helperI18N('noActiveBanners')
            };
        // Этот статус приходит в следующем кейсе:
        // у пользователя без ОС закончились деньги и он в интерфейсе нажал кнопку оплатить вбил сумму и нажал оплатить
        // в этом случае транзакция на самом деле не произошла,
        // для ее завершения необходимо пойти в баланс и подтвердить оплату там
        // и вот момент между этими двумя событиями и приходит этот статус
        // И поэтому ссылку на страницу Оплаты выводить не надо, ибо все уже было сделано в интерфейсе
        case GdCampaignPrimaryStatusDesc.WAIT_PAYMENT:
            return {
                text: helperI18N('waitPayment')
            };
        default:
            throw new Error(
                `Для типа подписи статуса ${hintType} не определен контент`
            );
    }
}

const moscowUtcOffset = 180;

export function formatLocalTimeWithMskTimezoneOffset(
    time: moment.Moment
): string {
    return moment(time)
        .utcOffset(time.utcOffset() - moscowUtcOffset)
        .format('Z');
}

/**
 * Формирует информацию о настройках таймтаргетинга кампании
 */
function getTimeTargetingStatusHint(
    timeTargetingSettings: TimeTargetingSettings
): string {
    const { status, startTime } = timeTargetingSettings;
    const offsetZoneString =
        startTime.utcOffset() === moscowUtcOffset
            ? ''
            : ` (MSK\u00A0${formatLocalTimeWithMskTimezoneOffset(startTime)})`;
    const timeParams = { time: startTime.format('HH:mm') };
    let result: string;

    switch (status) {
        case TimeTargetingStatus.today:
            result = helperI18N('showsWillStartAtTime', timeParams);
            break;

        case TimeTargetingStatus.tomorrow:
            result = helperI18N('showsWillStartTomorrowAtTime', timeParams);
            break;

        case TimeTargetingStatus.thisWeek: {
            const weekdayTexts = [
                helperI18N('showsWillStartOnMondayAtTime', timeParams),
                helperI18N('showsWillStartOnTuesdayAtTime', timeParams),
                helperI18N('showsWillStartOnWednesdayAtTime', timeParams),
                helperI18N('showsWillStartOnThursdayAtTime', timeParams),
                helperI18N('showsWillStartOnFridayAtTime', timeParams),
                helperI18N('showsWillStartOnSaturdayAtTime', timeParams),
                helperI18N('showsWillStartOnSundayAtTime', timeParams)
            ];

            result = weekdayTexts[startTime.weekday()];
            break;
        }

        default:
            result = helperI18N('showsWillStartOnDateAtTime', {
                ...timeParams,
                date: startTime.format('DD.MM')
            });
            break;
    }

    return result + offsetZoneString;
}

export function getCampaignCreateHref(
    type: CampaignType,
    login: string
): string {
    switch (type) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
        case CampaignType.CPM_BANNER:
        case CampaignType.CPM_DEALS:
        case CampaignType.CPM_YNDX_FRONTPAGE:
        case CampaignType.DYNAMIC:
        case CampaignType.MCBANNER:
        case CampaignType.MOBILE_CONTENT:
        case CampaignType.CONTENT_PROMOTION:
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
            return UrlHelpers.getCreateCampUrl(type, login);
        default:
            throw `Unsupported campaign type ${type}`;
    }
}

export function getInternalType(
    type: CampaignType | undefined
): CampaignType.INTERNAL_FREE | CampaignType.INTERNAL_DISTRIB | undefined {
    switch (type) {
        case CampaignType.INTERNAL_FREE:
            return CampaignType.INTERNAL_FREE;
        case CampaignType.INTERNAL_DISTRIB:
            return CampaignType.INTERNAL_DISTRIB;
        default:
            return;
    }
}
